import { styled } from '@ori-ui/mui';
import { MenuContentContainer as PbsMenuContent } from '@ori/personal-beauty-store';
import type { StaticNavigationNavigationItemDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { RefObject, FC } from 'react';
import { Suspense, memo, lazy } from 'react';

import { useActiveLink, usePbsContent } from '../../context/navigation';
import { useIsMobile } from '../../hooks';
import { SkeletonSubCategoryDesktop, SkeletonSubCategoryMobile } from '../Skeleton';
import { Catalogues } from './Catalogues/Catalogues';
import { BURGER_MENU_CONTENT_ROOT, CATEGORY_WRAPPER } from './constants';
import { useGetNavigationType } from './utils';

const BurgerMenuContentRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    width: '80%',
  },
}));

const StyledPbsMenuContent = styled(PbsMenuContent)(({ theme }) => ({
  marginLeft: '4rem',
  marginTop: '3rem',
}));

const LazyInspiration = lazy(async () => ({
  default: (await import('./Inspiration/Inspiration')).Inspiration,
}));
const LazyCategoryContent = lazy(async () => ({
  default: (await import('./CategoryContent/CategoryContent')).CategoryContent,
}));

const CategoryContent: FC<{ isMobile?: boolean }> = memo(({ isMobile }) => (
  <Suspense fallback={isMobile ? <SkeletonSubCategoryMobile /> : <SkeletonSubCategoryDesktop />}>
    <LazyCategoryContent />
  </Suspense>
));

export interface BurgerMenuContentProps {
  contentRootRef?: RefObject<HTMLDivElement>;
  link?: StaticNavigationNavigationItemDto;
}

export const BurgerMenuContent = ({ contentRootRef, link }: BurgerMenuContentProps) => {
  const isMobile = useIsMobile();
  const { activeLink } = useActiveLink();
  const { isShown } = usePbsContent();
  const { linkType, id } = activeLink ?? {};
  const { getTestId } = useTestId();
  const navigation = useGetNavigationType({
    activeLinkType: linkType ?? 'None',
    link,
  });

  return (
    <BurgerMenuContentRoot
      ref={contentRootRef}
      data-testid={getTestId(BURGER_MENU_CONTENT_ROOT)}
    >
      {isShown ? (
        <StyledPbsMenuContent />
      ) : navigation.isCatalogue ? (
        <Catalogues />
      ) : (
        <>
          {navigation.isCategory && id ? (
            <CategoryContent
              isMobile={isMobile}
              data-testid={getTestId(CATEGORY_WRAPPER)}
            />
          ) : null}
          <Suspense>{navigation.isInspiration ? <LazyInspiration /> : null}</Suspense>
        </>
      )}
    </BurgerMenuContentRoot>
  );
};
